export interface TagLike {
  id: number | string
  name: string
  color: string
}

const FALLBACK = '#AAAAAA'

const NAMED: Record<string, string> = {
  black: '#000000', white: '#ffffff', red: '#ff0000', green: '#008000',
  blue: '#0000ff', yellow: '#ffff00', orange: '#ffa500', purple: '#800080',
  grey: '#808080', gray: '#808080', pink: '#ffc0cb', brown: '#a52a2a',
  cyan: '#00ffff', magenta: '#ff00ff', teal: '#008080', navy: '#000080',
}

/**
 * `order_tags.color` is an unvalidated VARCHAR(20), so tolerate what a human
 * or an older UI might have stored: shorthand, a missing '#', an alpha suffix,
 * rgb(), or a bare color name. Returns null when nothing usable is present.
 */
function normalizeHex(color?: string | null): string | null {
  if (!color) return null
  const c = color.trim().toLowerCase()
  if (!c) return null

  if (NAMED[c]) return NAMED[c]

  const rgb = c.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/)
  if (rgb) {
    const [r, g, b] = [rgb[1], rgb[2], rgb[3]].map(n => Number(n))
    if ([r, g, b].every(n => n >= 0 && n <= 255)) return toHex([r!, g!, b!])
    return null
  }

  const body = c.startsWith('#') ? c.slice(1) : c
  if (!/^[0-9a-f]+$/.test(body)) return null

  // 8- and 4-digit forms carry alpha; drop it, the tint handles opacity.
  if (body.length === 8) return '#' + body.slice(0, 6)
  if (body.length === 6) return '#' + body
  if (body.length === 4 || body.length === 3) {
    return '#' + body.slice(0, 3).split('').map(ch => ch + ch).join('')
  }
  return null
}

function toRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

/** WCAG 2.1 relative luminance. */
function luminance(hex: string): number {
  const srgb = toRgb(hex).map(v => {
    const s = v / 255
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
  }) as [number, number, number]
  return 0.2126 * srgb[0] + 0.7152 * srgb[1] + 0.0722 * srgb[2]
}

/** WCAG contrast ratio between two hex colors. 1 = identical, 21 = black on white. */
export function contrastRatio(a: string, b: string): number {
  const la = luminance(a)
  const lb = luminance(b)
  const [hi, lo] = la > lb ? [la, lb] : [lb, la]
  return (hi + 0.05) / (lo + 0.05)
}

/**
 * Pick whichever of near-black / white reads better on `background`.
 * Chooses by measured contrast rather than a luminance threshold, so
 * mid-tone brand colors don't land on the wrong side of a cutoff.
 */
export function readableTextOn(background: string): string {
  const bg = normalizeHex(background) ?? FALLBACK
  const onDark = '#FFFFFF'
  const onLight = '#111111'
  return contrastRatio(bg, onLight) >= contrastRatio(bg, onDark) ? onLight : onDark
}

function toHex([r, g, b]: [number, number, number]): string {
  return '#' + [r, g, b].map(v => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, '0')).join('')
}

/** Composite `hex` at `alpha` over an opaque `base`. Returns an opaque hex. */
function mix(hex: string, base: string, alpha: number): string {
  const [r1, g1, b1] = toRgb(hex)
  const [r2, g2, b2] = toRgb(base)
  return toHex([
    r1 * alpha + r2 * (1 - alpha),
    g1 * alpha + g2 * (1 - alpha),
    b1 * alpha + b2 * (1 - alpha),
  ])
}

const SURFACE = '#FFFFFF'
const TINT_ALPHA = 0.14
const BORDER_ALPHA = 0.32
const TARGET_CONTRAST = 4.5

// A near-white tag tints to near-white: the chip and its border vanish against
// the card. Floor both against the surface so the chip is always a visible
// shape. Fill stays whisper-faint; the border does the work of defining it.
const MIN_FILL_CONTRAST = 1.04
const MIN_BORDER_CONTRAST = 1.55

/**
 * Darken `hex` toward black until it clears `target` contrast against `bg`.
 * Keeps the tag's hue in the text instead of falling back to flat black.
 */
function darkenUntilReadable(hex: string, bg: string, target = TARGET_CONTRAST): string {
  for (let k = 1; k >= 0; k -= 0.05) {
    const candidate = mix(hex, '#000000', k)
    if (contrastRatio(candidate, bg) >= target) return candidate
  }
  return '#000000'
}

/**
 * Subtle badge: a soft tint of the tag color as the chip, with the same hue
 * darkened just enough to stay readable on it. Avoids the loud, fully
 * saturated fill while keeping each tag visually distinct.
 */
export function tagBadgeStyle(color?: string | null): React.CSSProperties {
  const base = normalizeHex(color) ?? FALLBACK
  const bg = darkenUntilReadable(mix(base, SURFACE, TINT_ALPHA), SURFACE, MIN_FILL_CONTRAST)
  return {
    backgroundColor: bg,
    color: darkenUntilReadable(base, bg),
    borderColor: darkenUntilReadable(mix(base, SURFACE, BORDER_ALPHA), SURFACE, MIN_BORDER_CONTRAST),
  }
}

/** Look up a label name in the admin tag list. Case/whitespace insensitive. */
export function findTag<T extends TagLike>(label: string, tags: T[] = []): T | undefined {
  const key = label.trim().toLowerCase()
  return tags.find(t => t.name.trim().toLowerCase() === key)
}

/**
 * Style for a label by name. Falls back to a neutral grey chip when the
 * label is a free-text custom label with no matching admin tag.
 */
export function labelStyle(label: string, tags: TagLike[] = []): React.CSSProperties {
  return tagBadgeStyle(findTag(label, tags)?.color)
}

/**
 * `orders.labels` reaches the client as a real array (JSON column) or as a
 * JSON string (TEXT column), depending on the query. Accept both.
 */
export function parseLabels(raw: unknown): string[] {
  if (Array.isArray(raw)) return raw.filter((v): v is string => typeof v === 'string')
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const parsed = JSON.parse(raw)
      return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : []
    } catch {
      return []
    }
  }
  return []
}
